/* eslint-disable react-refresh/only-export-components */
// Charts — dependency-free, animated CSS/SVG chart components.
// Count-up numbers, SVG draw-on donut, animated bars + column timeline.
// Ported from the curro-redesign prototype (charts.jsx); rows come from the
// live /api/breakdown shape: [{ key, spend, lines }].

import { useEffect, useRef, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { fmtR, fmtRm, fmtN, PALETTE } from "../lib/format.js";
import { usePrinting } from "./PrintContext.jsx";

/* Fires once when the element scrolls into view — so chart animations play
   when the user actually sees them (not while still off-screen on load).
   Falls back to "visible" where IntersectionObserver isn't available. */
export function useInView({ threshold = 0.2 } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const printing = usePrinting();   // when exporting to PDF, treat everything as visible
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView || printing];
}

/* count-up that runs once on mount / when value changes */
export function useCountUp(target, dur = 900) {
  const [v, setV] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setV(target); return; }   // eslint-disable-line react-hooks/set-state-in-effect
    let raf, start;
    const from = ref.current ?? 0;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setV(from + (target - from) * e);
      if (p < 1) raf = requestAnimationFrame(step);
      else ref.current = target;
    };
    raf = requestAnimationFrame(step);
    // safety net: if rAF is throttled (background tab), still land on target
    const safety = setTimeout(() => setV(target), dur + 400);
    return () => { cancelAnimationFrame(raf); clearTimeout(safety); };
  }, [target, dur]);
  return v;
}

export function CountUp({ value, fmt = (x) => Math.round(x).toLocaleString(), dur }) {
  const [ref, inView] = useInView();
  const printing = usePrinting();
  const v = useCountUp(inView ? value : 0, dur);   // counts up once it scrolls into view
  // In print/PDF export, show the final value immediately (no mid-animation capture).
  return <span ref={ref}>{fmt(printing ? value : v)}</span>;
}

/* ---------------- Donut (SVG, draws on mount) ---------------- */
export function Donut({ rows, topN = 8, size = 188, thickness = 26, centerTop, centerSub, palette = PALETTE, onSelect }) {
  const [ref, inView] = useInView();
  const [on, setOn] = useState(false);
  const [hover, setHover] = useState(null);
  const printing = usePrinting();
  // Draw the ring once it scrolls into view (re-draws if the data changes while visible).
  useEffect(() => {
    if (!inView) { setOn(false); return; }   // eslint-disable-line react-hooks/set-state-in-effect
    setOn(false);
    const t = setTimeout(() => setOn(true), 60);
    return () => clearTimeout(t);
  }, [inView, rows]);

  const positive = rows.filter((r) => r.spend > 0);
  const total = positive.reduce((s, r) => s + r.spend, 0) || 1;
  const top = positive.slice(0, topN);
  const rest = positive.slice(topN);
  const items = [
    ...top.map((r, i) => ({ label: r.label ?? r.key, key: r.key, value: r.spend, color: palette[i % palette.length] })),
    ...(rest.length ? [{ label: `Others (${rest.length})`, key: null, value: rest.reduce((s, r) => s + r.spend, 0), color: "#9DA790" }] : []),
  ].map((s) => ({ ...s, pct: (s.value / total) * 100 }));
  const pick = (s) => { if (onSelect && s.key) onSelect(s.key); };

  if (items.length === 0) return null;

  const r = (size - thickness) / 2 - 6;
  const cx = size / 2;
  const C = 2 * Math.PI * r;
  let acc = 0;
  const segs = items.map((s) => {
    const len = (s.pct / 100) * C;
    const startDeg = (acc / 100) * 360;
    acc += s.pct;
    return { ...s, len, startDeg };
  });
  // `hover` is an index into segs; after a cross-filter the data (and segs) can
  // shrink while a stale hover index lingers, so look it up defensively.
  const hovered = hover != null ? segs[hover] : null;

  return (
    <div className="donut-row" ref={ref}>
      <div className="donut" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--track)" strokeWidth={thickness} />
          {segs.map((s, i) => (
            <circle key={i} cx={cx} cy={cx} r={r} fill="none" stroke={s.color}
              strokeWidth={hover === i ? thickness + 5 : thickness}
              strokeDasharray={`${s.len} ${C}`}
              strokeDashoffset={on || printing ? 0 : s.len}
              strokeLinecap="butt"
              transform={`rotate(${s.startDeg} ${cx} ${cx})`}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onClick={() => pick(s)}
              style={{ transition: printing ? "none" : `stroke-dashoffset .8s cubic-bezier(.22,.61,.36,1) ${i * 0.07}s, stroke-width .2s ease`, cursor: onSelect && s.key ? "pointer" : "default" }} />
          ))}
        </svg>
        <div className="center">
          <b>{hovered ? `${hovered.pct.toFixed(1)}%` : centerTop}</b>
          <span>{hovered ? hovered.label.slice(0, 16) : centerSub}</span>
        </div>
      </div>
      <ul className="legend">
        {segs.map((s, i) => (
          <li key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            onClick={() => pick(s)}
            style={{ opacity: hover == null || hover === i ? 1 : 0.45, transition: "opacity .2s", cursor: onSelect && s.key ? "pointer" : "default" }}>
            <i style={{ background: s.color }} />
            <span className="lname">{s.label}</span>
            <b>{fmtRm(s.value)}</b>
            <small>{s.pct.toFixed(1)}%</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Horizontal bars ---------------- */
export function HBars({ rows, topN = 10, fmt = fmtRm, palette = PALETTE, colorByIndex = false, onSelect, expandable = false, step = 10, subPct = false }) {
  const [ref, inView] = useInView();
  const printing = usePrinting();
  const [limit, setLimit] = useState(topN);
  useEffect(() => { setLimit(topN); }, [topN, rows]);   // reset the reveal when the data/cap changes
  const cap = expandable ? limit : topN;
  const display = rows.slice(0, cap);
  const hidden = rows.length - display.length;
  const max = Math.max(1, ...display.map((r) => Math.abs(r.spend)));
  return (
    <div className="hbars" ref={ref}>
      {display.map((r, i) => {
        const w = (Math.abs(r.spend) / max) * 100;
        // A negative value (e.g. a loss-making raw material's margin) reads as a LOSS, never a big
        // contributor — override to a red fill so the bar's meaning matches its sign.
        const fill = r.spend < 0 ? "linear-gradient(90deg,#D64C46,#F0938E)"
          : colorByIndex ? palette[i % palette.length]
          : i === 0 ? "linear-gradient(90deg,var(--curro-blue),var(--curro-sky))"
          : "linear-gradient(90deg,var(--curro-sky),#6FD0EE)";
        return (
          <div className={`hbar${onSelect ? " clickable" : ""}`} key={r.key}
            onClick={onSelect ? () => onSelect(r.key) : undefined} title={onSelect ? `Filter to ${r.label ?? r.key}` : undefined}>
            <div className="nm">{r.label ?? r.key}</div>
            <div className="tk">
              {/* grow from 0 → width once in view, so the bar animation is seen */}
              <div className="fl" style={{ width: inView ? `${w}%` : "0%", background: fill,
                transition: printing ? "none" : `width .7s cubic-bezier(.22,.61,.36,1) ${i * 0.04}s` }} />
            </div>
            <div className="vl"><span className="vl-amt">{fmt(r.spend)}</span>{subPct && r.marginPct != null ? <span className="vl-pct">{r.marginPct}%</span> : null}</div>
          </div>
        );
      })}
      {expandable && hidden > 0 && (
        <button type="button" onClick={() => setLimit((l) => l + step)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)",
            font: "600 12px var(--font-body)", padding: "6px 0", textAlign: "left" }}>
          + {hidden} more · show {Math.min(step, hidden)}
        </button>
      )}
    </div>
  );
}

/* ---------------- Month timeline (Recharts bar chart) ---------------- */
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Keys arrive as "%b %Y" (e.g. "Feb 2024"); also tolerate ISO "2024-02".
const parseMonth = (key) => {
  const s = String(key).trim();
  let m = /^([A-Za-z]{3})[A-Za-z]*\s+(\d{4})$/.exec(s);
  if (m) { const mon = MONTH_NAMES.findIndex((n) => n.toLowerCase() === m[1].toLowerCase()); if (mon >= 0) return { mon: mon + 1, yy: m[2].slice(2) }; }
  m = /^(\d{4})-(\d{2})/.exec(s);
  if (m) return { mon: Number(m[2]), yy: m[1].slice(2) };
  return null;
};

// Fixed dark tooltip — legible over both light and dark charts (var(--ink)
// would be light-on-light in dark mode).
function MonthTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: "#13241A", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, padding: "8px 11px", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,.3)" }}>
      <div style={{ fontWeight: 700, color: "#fff", marginBottom: 2 }}>{d.full}</div>
      <div style={{ color: "#CDE39E", fontWeight: 600 }}>{fmtR(d.revenue)}</div>
      {d.lines != null && <div style={{ color: "#9DB98A", fontSize: 11 }}>{fmtN(d.lines)} lines</div>}
    </div>
  );
}

// Left inset of the plot = YAxis width (46) + chart left margin (4); right inset = right margin (8).
const TL_PLOT_LEFT = 50, TL_PLOT_RIGHT = 8;

export function Timeline({ rows, onSelect }) {
  const [ref, inView] = useInView();
  const printing = usePrinting();
  // Animate the bars growing in ONCE (first paint). On later data changes
  // (cross-filter / clear) skip the entrance animation so bars snap to their new
  // heights instead of flashing empty→grow each time.
  const [animate, setAnimate] = useState(true);
  const months = rows.filter((r) => r.key !== "(none)");
  if (months.length === 0) return null;
  const peak = Math.max(...months.map((r) => r.spend));
  // Every month's axis label carries its year (angled so they don't collide), e.g.
  // "Mar '25" — so a month is never ambiguous about which year it belongs to. (The
  // old scheme stamped the year only at January, which made mid-year months read as
  // the previous year.) Exact value/lines are still shown on hover.
  const data = months.map((r) => {
    const p = parseMonth(r.key);
    const label = p ? `${MONTH_NAMES[p.mon - 1]} '${p.yy}` : String(r.key).slice(0, 3);
    return { label, full: String(r.key), revenue: r.spend, lines: r.lines, isPeak: r.spend === peak };
  });
  // Click anywhere in a month's vertical band (not just the bar, which can be tiny) →
  // map the click's x to the column index. Runs on the wrapper so the tooltip (SVG
  // hover) still works.
  const handleClick = (e) => {
    if (!onSelect || !ref.current) return;
    const box = ref.current.getBoundingClientRect();
    const left = box.left + TL_PLOT_LEFT, right = box.right - TL_PLOT_RIGHT;
    if (e.clientX < left || e.clientX > right || data.length === 0) return;
    const idx = Math.min(data.length - 1, Math.max(0, Math.floor((e.clientX - left) / ((right - left) / data.length))));
    const full = data[idx]?.full;
    if (full) onSelect(full);
  };
  return (
    <div ref={ref} onClickCapture={onSelect ? handleClick : undefined} className="chart-body"
      style={{ width: "100%", height: printing ? 220 : 280, cursor: onSelect ? "pointer" : undefined }}>
      {inView && (
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 12, right: 8, bottom: 30, left: 4 }} barCategoryGap="18%">
          <CartesianGrid stroke="rgba(120,140,170,0.15)" vertical={false} />
          {/* interval="preserveStartEnd" lets Recharts auto-thin the (now year-stamped) labels
              so they never overlap — dense charts show fewer ticks, all unambiguous; hover gives the exact month. */}
          <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={4} angle={-45} textAnchor="end" height={52}
            tick={{ fontSize: 10, fill: "var(--muted)" }} tickLine={false} axisLine={{ stroke: "rgba(120,140,170,0.3)" }} />
          <YAxis width={46} tickFormatter={(v) => `R${(v / 1e6).toFixed(0)}m`}
            tick={{ fontSize: 11, fill: "var(--muted)" }} tickLine={false} axisLine={false} />
          <Tooltip content={<MonthTooltip />} cursor={{ fill: "rgba(0,114,206,0.06)" }} />
          <Bar dataKey="revenue" radius={[6, 6, 0, 0]} maxBarSize={34}
            isAnimationActive={animate && !printing} animationBegin={120}
            onAnimationEnd={() => setAnimate(false)}>
            {data.map((d, i) => <Cell key={i} fill={d.isPeak ? "var(--curro-yellow)" : "var(--curro-blue)"} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      )}
    </div>
  );
}

/* ============================================================
   A/B compare charts (Explore compare mode). Fills render at their FINAL width;
   the entrance animation replays via a CSS keyframe when the wrapper is re-keyed
   (animKey) on a data change — so a frozen clock can't strand a fill empty.
   ============================================================ */
const gcMonthLabel = (k) => { const p = parseMonth(k); return p ? MONTH_NAMES[p.mon - 1] : String(k).slice(0, 3); };

/* paired A/B horizontal bars — A=blue, B=violet; top-N + exact Others roll-up */
export function PairedBars({ rows, topN = 12, onSelect, showMarginPct = false }) {
  const sorted = [...rows].sort((x, y) => (Math.abs(y.a) + Math.abs(y.b)) - (Math.abs(x.a) + Math.abs(x.b)));
  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const items = [...top];
  if (rest.length) items.push({ key: `Others (${rest.length})`, others: true, a: rest.reduce((s, r) => s + r.a, 0), b: rest.reduce((s, r) => s + r.b, 0), arev: rest.reduce((s, r) => s + (r.arev || 0), 0), brev: rest.reduce((s, r) => s + (r.brev || 0), 0) });
  const max = Math.max(1, ...items.flatMap((r) => [Math.abs(r.a), Math.abs(r.b)]));
  const w = (v) => `${Math.min(100, (Math.abs(v) / max) * 100).toFixed(1)}%`;
  // optional margin % (amount ÷ revenue) appended to each side's value label
  const pctOf = (v, rev) => (showMarginPct && rev ? ` · ${((v / rev) * 100).toFixed(1)}%` : "");
  return (
    <div className="pbars">
      {items.map((r) => {
        const clickable = onSelect && !r.others;
        return (
          <div className={`pbar-grp${r.others ? " others" : ""}${clickable ? " clickable" : ""}`} key={r.key}
            onClick={clickable ? () => onSelect(r.key) : undefined}
            title={clickable ? `Filter both views to ${r.label ?? r.key}` : (r.label ?? r.key)}
            style={clickable ? { cursor: "pointer" } : undefined}>
            <div className="pbar-name">{r.label ?? r.key}</div>
            <div className="pbar-tracks">
              <div className="pbar-row"><span className="pbar-tag a">A</span><div className="pbar-tk"><i className="a" style={{ width: w(r.a) }} /></div><span className="pbar-vl">{fmtRm(r.a)}{pctOf(r.a, r.arev)}</span></div>
              <div className="pbar-row"><span className="pbar-tag b">B</span><div className="pbar-tk"><i className="b" style={{ width: w(r.b) }} /></div><span className="pbar-vl">{fmtRm(r.b)}{pctOf(r.b, r.brev)}</span></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* diverging Δ (B − A) — one bar per key, green right / red left of a zero centre,
   sorted by |Δ|. rows: [{ key, a, b, delta, pct }] */
export function DivergingDelta({ rows, topN = 18, onSelect }) {
  const sorted = [...rows].filter((r) => r.delta !== 0).sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta)).slice(0, topN);
  if (sorted.length === 0) return <p className="hint">No differences between the two views.</p>;
  const max = Math.max(1, ...sorted.map((r) => Math.abs(r.delta)));
  const w = (v) => `${Math.min(50, (Math.abs(v) / max) * 50).toFixed(2)}%`;
  return (
    <div className="dvg">
      <div className="dvg-zero-key"><span className="lo">← less in B</span><span className="hi">more in B →</span></div>
      {sorted.map((r) => {
        const pos = r.delta >= 0;
        return (
          <div className="dvg-row" key={r.key}
            onClick={onSelect ? () => onSelect(r.key) : undefined}
            title={onSelect ? `Filter both views to ${r.label ?? r.key}` : undefined}
            style={onSelect ? { cursor: "pointer" } : undefined}>
            <div className="dvg-name" title={r.label ?? r.key}>{r.label ?? r.key}</div>
            <div className="dvg-track"><div className={`dvg-bar ${pos ? "pos" : "neg"}`} style={{ width: w(r.delta) }} /></div>
            <div className="dvg-fig" style={{ color: pos ? "var(--pos)" : "var(--neg)" }}>
              <span>{pos ? "+" : "−"}{fmtRm(Math.abs(r.delta))}</span>
              <small>{r.pct == null ? "new" : `${r.pct >= 0 ? "+" : ""}${r.pct.toFixed(0)}%`}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* two donuts side by side (share-shift A vs B); rowsA/rowsB share a key order */
export function CompareDonuts({ rowsA, rowsB, onSelect }) {
  const totA = rowsA.reduce((s, r) => s + Math.max(0, r.spend), 0);
  const totB = rowsB.reduce((s, r) => s + Math.max(0, r.spend), 0);
  return (
    <div className="cmp-donuts">
      <div className="cmp-donut-card">
        <div className="cd-head"><span className="cd-dot" style={{ background: "var(--curro-blue)" }} /> View A</div>
        <Donut rows={rowsA} topN={7} size={172} centerTop={fmtRm(totA)} centerSub="View A" onSelect={onSelect} />
      </div>
      <div className="cmp-donut-card">
        <div className="cd-head"><span className="cd-dot" style={{ background: "var(--curro-violet)" }} /> View B</div>
        <Donut rows={rowsB} topN={7} size={172} centerTop={fmtRm(totB)} centerSub="View B" onSelect={onSelect} />
      </div>
    </div>
  );
}

/* grouped column timeline — A + B series per month (run-vs-run / seasonality) */
export function GroupedColumns({ rows, order, onSelect }) {
  const [hover, setHover] = useState(null);   // hovered/tapped month index → floating tooltip
  const ordered = order.map((k) => rows.find((r) => r.key === k)).filter(Boolean);
  if (ordered.length === 0) return null;
  const max = Math.max(1, ...ordered.flatMap((m) => [m.a, m.b]));
  return (
    <div className="gcols">
      <div className="ab-legend"><span className="it"><span className="sw a" /> View A</span><span className="it"><span className="sw b" /> View B</span></div>
      <div className="gcols-plot" onMouseLeave={() => setHover(null)}>
        {ordered.map((m, i) => {
          const d = m.delta ?? (m.b - m.a);
          return (
            <div className={`gcol${hover === i ? " on" : ""}`} key={m.key}
              title={onSelect ? `Filter both views to ${gcMonthLabel(m.key)}` : undefined}
              style={onSelect ? { cursor: "pointer" } : undefined}
              onMouseEnter={() => setHover(i)}
              onClick={onSelect ? () => onSelect(m.key) : () => setHover((h) => (h === i ? null : i))}>
              {hover === i && (
                <div className="gcol-tip">
                  <b>{gcMonthLabel(m.key)}</b>
                  <span><i className="sw a" /> View A <em>{fmtRm(m.a)}</em></span>
                  <span><i className="sw b" /> View B <em>{fmtRm(m.b)}</em></span>
                  <span className="d" style={{ color: d >= 0 ? "#8BE0A0" : "#F0A38F" }}>Δ {d >= 0 ? "+" : "−"}{fmtRm(Math.abs(d))}</span>
                </div>
              )}
              <div className="gcol-pair">
                <div className="gcol-stem a" style={{ height: `${(m.a / max) * 100}%` }} />
                <div className="gcol-stem b" style={{ height: `${(m.b / max) * 100}%` }} />
              </div>
              <div className="gcol-lbl">{gcMonthLabel(m.key)}</div>
            </div>
          );
        })}
      </div>
      <div className="axis" />
    </div>
  );
}
